import type { LatLng, LocationChange, MapPoint, PinEntry, PointSource, StopTank } from "./api";
import { readingTime } from "./format";

export const NAVIGATE_URL = "https://www.google.com/maps/dir/?api=1&destination=";
/** The night job refreshes a geocoded point after these days (REFRESH_DAYS in app/geo.py). */
export const GEOCODE_REFRESH_DAYS = 25;
export const POOR_ACCURACY_M = 50;
const DAY_MS = 86_400_000;

export const SOURCE_TEXT: Record<PointSource, string> = {
  geocode: "Geocoded from the address",
  manual: "Placed by hand",
  seed: "Synthetic demo point",
  gps: "Phone GPS",
  sheet: "From the service sheet",
};

/** Business rule: drivers go to the tank pin, else the front door, else the address. */
export function navTarget(stop: { tank?: LatLng | null; door?: LatLng | null; address: string }): LatLng | string | null {
  if (stop.tank) return { lat: stop.tank.lat, lng: stop.tank.lng };
  if (stop.door) return { lat: stop.door.lat, lng: stop.door.lng };
  const address = stop.address.trim();
  return address ? address : null;
}

export function navigationUrl(target: LatLng | string | null): string | null {
  if (target === null) return null;
  if (typeof target === "string") return NAVIGATE_URL + encodeURIComponent(target);
  return `${NAVIGATE_URL}${coordinate(target.lat)},${coordinate(target.lng)}`;
}

/** Six decimals is about 10 cm, more than a phone GPS can give. */
export const coordinate = (value: number) => String(Number(value.toFixed(6)));

export function parsePoint(latText: string, lngText: string): LatLng | { error: string; field: "lat" | "lng" } {
  const lat = Number(latText.trim());
  const lng = Number(lngText.trim());
  if (!latText.trim() || !Number.isFinite(lat) || lat < -90 || lat > 90) return { error: "Enter a latitude from -90 to 90.", field: "lat" };
  if (!lngText.trim() || !Number.isFinite(lng) || lng < -180 || lng > 180) return { error: "Enter a longitude from -180 to 180.", field: "lng" };
  if (lat === 0 && lng === 0) return { error: "Latitude 0 and longitude 0 is not a site. Enter the real point.", field: "lat" };
  return { lat, lng };
}

/** The change that puts a tank pin back as it was, with its source and accuracy. */
export function tankUndo(before: StopTank | null | undefined): LocationChange {
  if (!before) return { point: "tank", reset: true };
  const source = before.source === "gps" || before.source === "seed" ? before.source : "manual";
  return { point: "tank", lat: before.lat, lng: before.lng, source, accuracy_m: source === "gps" ? (before.accuracy_m ?? null) : null };
}

/** The change that puts a door back: a geocoded door is geocoded again, as Google terms keep no copy of it. */
export function doorUndo(before: MapPoint | null | undefined): LocationChange | null {
  if (!before) return null;
  if (before.source === "geocode") return { point: "door", reset: true };
  return { point: "door", lat: before.lat, lng: before.lng, source: "manual" };
}

const mercatorY = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)) / (2 * Math.PI);

/** The highest whole zoom that shows `other` on a map of this size centred on `at`, with a margin in pixels. */
export function zoomToShow(at: LatLng, other: LatLng, width: number, height: number, margin = 40): number {
  const dx = (Math.abs(other.lng - at.lng) / 360) * 256;
  const dy = Math.abs(mercatorY(other.lat) - mercatorY(at.lat)) * 256;
  const fit = (half: number, delta: number) => (delta > 0 ? Math.log2(Math.max(half - margin, 1) / delta) : Infinity);
  return Math.max(3, Math.floor(Math.min(fit(width / 2, dx), fit(height / 2, dy), 22)));
}

export const samePoint = (a: LatLng | null | undefined, b: LatLng | null | undefined) =>
  (!a && !b) || Boolean(a && b && coordinate(a.lat) === coordinate(b.lat) && coordinate(a.lng) === coordinate(b.lng));

/** A geocoded door that the night job refreshes soon, or a refresh that did not happen. */
export function geocodeStale(point: Pick<MapPoint, "source" | "geocoded_at"> | null | undefined, now: Date): boolean {
  if (!point || point.source !== "geocode" || !point.geocoded_at) return false;
  return now.getTime() - new Date(point.geocoded_at).getTime() >= GEOCODE_REFRESH_DAYS * DAY_MS;
}

export function accuracyText(meters: number | null | undefined): string {
  if (meters === null || meters === undefined) return "";
  const shown = `±${Math.round(meters)} m`;
  return meters > POOR_ACCURACY_M ? `${shown}. Poor accuracy: go outside or nearer to the container, then try again.` : shown;
}

/** The point a pin shows: the tank pin falls back to the front door until someone sets it. */
export const shownTank = (tank: LatLng | null | undefined, door: LatLng | null | undefined): LatLng | null => tank ?? door ?? null;

export const HISTORY_SOURCE_TEXT: Record<PointSource, string> = {
  geocode: "Geocoded",
  manual: "By hand",
  seed: "Demo point",
  gps: "GPS",
  sheet: "Sheet",
};

const pointOfEntry = (entry: PinEntry): StopTank | null =>
  entry.lat === null || entry.lng === null || !entry.source ? null : { lat: entry.lat, lng: entry.lng, source: entry.source, accuracy_m: entry.accuracy_m };

/** The change that puts the tank pin back at a history entry, or null when the entry has no point. */
export function restoreOf(entry: PinEntry): LocationChange | null {
  const point = pointOfEntry(entry);
  return point ? tankUndo(point) : null;
}

const tenths = (meters: number | null | undefined) => (meters === null || meters === undefined ? null : Math.round(meters * 10) / 10);

/** The pin is at this entry now: the same point, source and accuracy. */
export function hereNow(entry: PinEntry, tank: StopTank | null | undefined): boolean {
  const point = pointOfEntry(entry);
  if (!point || !tank) return entry.cleared && !tank;
  return samePoint(point, tank) && (!tank.source || tank.source === point.source) && tenths(point.accuracy_m) === tenths(tank.accuracy_m);
}

export function historyWhen(entry: PinEntry, timeZone: string): string {
  return entry.at ? readingTime(entry.at, timeZone) : "Before the first change";
}

export function historySource(entry: PinEntry): string {
  return entry.cleared || !entry.source ? "Cleared" : HISTORY_SOURCE_TEXT[entry.source];
}

export function historyPoint(entry: PinEntry): string {
  if (entry.cleared) return "–";
  if (entry.lat === null || entry.lng === null) return "Not kept";
  return `${entry.lat.toFixed(5)}, ${entry.lng.toFixed(5)}`;
}

export const historyAccuracy = (entry: PinEntry) => (entry.accuracy_m === null || entry.cleared ? "–" : `±${Math.round(entry.accuracy_m)} m`);

export function restoredText(entry: PinEntry, timeZone: string, pin = "the container pin"): string {
  return `Saved: ${pin} is back at the point ${entry.at ? `of ${readingTime(entry.at, timeZone)}` : "before the first change"}.`;
}

export const ROUGH_PIN = "Rough pin";
/** The amber tag of a rough pin: the same chip as the other warnings. */
export const ROUGH_PIN_CLASS = "chip-flag serious";

/** The words of the button and the line for each point of a location card: "Change door location". */
export function pointWords(word: string) {
  return {
    change: `Change ${word} location`,
    save: `Save ${word} location`,
    editing: `Editing the ${word} location`,
    saved: `Saved: the ${word} location.`,
  };
}
